import React, { useContext } from 'react';
import { useTranslation } from 'react-i18next';
import USStateSelector from '../../../../components/USStateDropdown';
import CountryDropdown from '../../../../components/CountryDropdown';
import useCheckoutPaths from '../../../../hooks/useCheckoutPaths';
import LoginButton from '../../components/LoginButton';
import styles from './Shipping.module.scss';
import LocaleContext, { FiCountry } from '../../../../contexts/LocaleContext';
import Chooser from '../../../../components/Chooser';

interface ChangeHandler {
  onChange(fieldName: string, value: string): void;
}

interface AccountFields {
  email?: string;
  passwordlessCheckoutEnabled?: boolean;
}

interface ContactInfoFields {
  firstName?: string;
  lastName?: string;
  phone?: string;
  showPhoneNumberInput: boolean;
  // For passwordless checkout, email is included in ContactInfo instead of AccountDetails
  email?: string;
  includeEmailField?: boolean;
  showSignInLink?: boolean;
}

interface GiftContactInfoFields {
  firstName?: string;
  lastName?: string;
}

interface ShippingAddressFields {
  line1?: string;
  line2?: string;
  city?: string;
  zip?: string;
  state?: string;
  phone?: string;
}

interface ShippingAddressDetailsProps extends ShippingAddressFields, ChangeHandler {
  title: string;
  includePhoneField?: boolean;
}

export enum GiftShippingOption {
  ShipToYourAddress = 'ship_to_your_address',
  ShipToSomeoneElse = 'ship_to_someone_else',
}

export function AccountDetails({ email, passwordlessCheckoutEnabled, onChange }: AccountFields & ChangeHandler) {
  const { t } = useTranslation();
  const checkoutPaths = useCheckoutPaths();

  return (
    <div className={styles.formSection}>
      <h4>{t('create_an_account')}</h4>
      <input
        type="email"
        name="email"
        placeholder={t('email_address')}
        value={email}
        onChange={(e) => onChange(e.target.name, e.target.value)}
        autoComplete="email"
        data-hj-whitelist
      />
      {!passwordlessCheckoutEnabled && (
        <input
          type="password"
          name="password"
          placeholder={t('password')}
          onChange={(e) => onChange(e.target.name, e.target.value)}
          autoComplete="new-password"
        />
      )}
      <div className={styles.loginButton}>
        <LoginButton returnTo={checkoutPaths.Shipping} />
      </div>
    </div>
  );
}

export function ShippingGiftRecipientInformation({
  onChange,
  firstName,
  lastName,
}: GiftContactInfoFields & ChangeHandler) {
  const { t } = useTranslation();
  return (
    <div className={styles.formSection}>
      <h4>{"Recipient's Information"}</h4>
      <div className={styles.formRow}>
        <input
          type="text"
          name="giftFirstName"
          placeholder={t('first_name')}
          value={firstName}
          onChange={(e) => onChange(e.target.name, e.target.value)}
          autoComplete="given-name"
          data-hj-whitelist
        />
        <input
          type="text"
          name="giftLastName"
          placeholder={t('last_name')}
          value={lastName}
          onChange={(e) => onChange(e.target.name, e.target.value)}
          autoComplete="family-name"
          data-hj-whitelist
        />
      </div>
    </div>
  );
}

export const ShippingGiftOption = ({
  giftShippingOption,
  setGiftShippingOption,
}: {
  giftShippingOption: GiftShippingOption;
  setGiftShippingOption: (option: GiftShippingOption) => void;
}) => {
  return (
    <div className={styles.main} style={{ paddingBottom: '32px' }}>
      <Chooser
        onSelect={(selected: GiftShippingOption) => {
          setGiftShippingOption(selected);
        }}
        selectedOption={giftShippingOption}
        groups={[
          {
            options: [
              {
                content: 'Ship to your address',
                value: GiftShippingOption.ShipToYourAddress,
              },
              {
                content: 'Ship to someone else',
                value: GiftShippingOption.ShipToSomeoneElse,
              },
            ],
          },
        ]}
        showRadio
        autoHeight={true}
      />
    </div>
  );
};

export function ContactInfo({
  onChange,
  firstName,
  lastName,
  phone,
  showPhoneNumberInput: requiresShippingDetails,
  email,
  includeEmailField,
  showSignInLink,
}: ContactInfoFields & ChangeHandler) {
  const { t } = useTranslation();
  const checkoutPaths = useCheckoutPaths();
  return (
    <div className={styles.formSection}>
      <h4>{t('contact_info')}</h4>
      {includeEmailField && (
        <>
          <input
            type="email"
            name="email"
            placeholder={t('email_address')}
            value={email}
            onChange={(e) => onChange(e.target.name, e.target.value)}
            autoComplete="email"
            data-hj-whitelist
          />
          {showSignInLink && (
            <div className={styles.loginButton}>
              <LoginButton returnTo={checkoutPaths.Shipping} />
            </div>
          )}
        </>
      )}
      <div className={styles.formRow}>
        <input
          type="text"
          name="firstName"
          placeholder={t('first_name')}
          value={firstName}
          onChange={(e) => onChange(e.target.name, e.target.value)}
          autoComplete="given-name"
          data-hj-whitelist
        />
        <input
          type="text"
          name="lastName"
          placeholder={t('last_name')}
          value={lastName}
          onChange={(e) => onChange(e.target.name, e.target.value)}
          autoComplete="family-name"
          data-hj-whitelist
        />
      </div>
      {requiresShippingDetails && (
        <input
          type="tel"
          name="phone"
          placeholder={t('phone_number')}
          value={phone}
          onChange={(e) => onChange(e.target.name, e.target.value)}
          autoComplete="tel"
          data-hj-whitelist
        />
      )}
    </div>
  );
}

export function ShippingAddressDetails({
  onChange,
  line1,
  line2,
  city,
  zip,
  state,
  phone,
  includePhoneField,
  title,
}: ShippingAddressDetailsProps) {
  const { t } = useTranslation();
  const { country } = useContext(LocaleContext);

  let zipPlaceholder: string;
  switch (country) {
    case FiCountry.CA:
      zipPlaceholder = t('postal_code');
      break;
    case FiCountry.GB:
      zipPlaceholder = t('post_code');
      break;
    case FiCountry.US:
    default:
      zipPlaceholder = t('zip_code');
  }

  let addressLine1Placeholder: string;
  switch (country) {
    case FiCountry.GB:
      addressLine1Placeholder = t('address_line_1');
      break;
    case FiCountry.CA:
    case FiCountry.US:
    default:
      addressLine1Placeholder = t('street_address');
  }

  let addressLine2Placeholder: string;
  switch (country) {
    case FiCountry.GB:
      addressLine2Placeholder = t('address_line_2_optional');
      break;
    case FiCountry.CA:
    case FiCountry.US:
    default:
      addressLine2Placeholder = t('apt_suite_other_optional');
  }

  let cityPlaceholder: string;
  switch (country) {
    case FiCountry.GB:
      cityPlaceholder = t('town_or_city');
      break;
    case FiCountry.CA:
    case FiCountry.US:
    default:
      cityPlaceholder = t('city');
  }

  const stateDropownEnabled = country === FiCountry.US || country === FiCountry.CA;

  return (
    <div className={styles.formSection}>
      <h4>{title}</h4>
      <input
        type="text"
        name="line1"
        placeholder={addressLine1Placeholder}
        value={line1}
        onChange={(e) => onChange(e.target.name, e.target.value)}
        autoComplete="address-line1"
        data-hj-whitelist
      />
      <input
        type="text"
        name="line2"
        placeholder={addressLine2Placeholder}
        value={line2}
        onChange={(e) => onChange(e.target.name, e.target.value)}
        autoComplete="address-line-2"
        data-hj-whitelist
      />
      <input
        type="text"
        name="city"
        placeholder={cityPlaceholder}
        value={city}
        onChange={(e) => onChange(e.target.name, e.target.value)}
        autoComplete="address-level2"
        data-hj-whitelist
      />
      <div className={styles.formRow}>
        {stateDropownEnabled ? (
          <USStateSelector state={state} onChange={(newState) => onChange('state', newState)} />
        ) : (
          <input
            type="text"
            name="state"
            placeholder={t('county')}
            value={state}
            onChange={(e) => onChange(e.target.name, e.target.value)}
            autoComplete="address-level1"
            data-hj-whitelist
          />
        )}
        <input
          type="text"
          name="zip"
          placeholder={zipPlaceholder}
          value={zip}
          onChange={(e) => onChange(e.target.name, e.target.value)}
          autoComplete="postal-code"
          data-hj-whitelist
        />
      </div>
      {includePhoneField && (
        <>
          <input
            type="tel"
            name="phone"
            placeholder={t('phone_number')}
            value={phone}
            onChange={(e) => onChange(e.target.name, e.target.value)}
            autoComplete="tel"
            data-hj-whitelist
          />
          <div className={styles.phoneNumberDescription}>
            {t('a_phone_number_can_be_helpful_if_there_are_any_delivery_questions_from_the_carrier')}
          </div>
        </>
      )}
      <CountryDropdown country={country} onChange={() => {}} disabled />
    </div>
  );
}
